import React, { useState } from 'react';
import Btn from '../common/Btn';
import ConfirmDialog from './ConfirmDialog';
import { setAlert } from '../../helpers/alerts';
import { apiDeleteBranch, apiUpdateBranch, apiTriggerCreateBranch } from '../../api/branches';

const ICO_BRANCH  = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_MOON    = 'M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36A5.39 5.39 0 0 1 12.5 3.1 9.06 9.06 0 0 0 12 3z';
const ICO_CHECK   = 'M20 6 9 17l-5-5';
const ICO_TRASH   = 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6';
const ICO_TAG     = 'M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01';

const EMPTY_DIALOG = { open: false, title: '', message: '', danger: false, onConfirm: () => {} };

export default function ActionsForBranch({ branch, reloadData, size = 'lg', showToReleaseAction = false }) {
  const [dialog, setDialog] = useState(EMPTY_DIALOG);

  const confirm = (opts) => setDialog({ open: true, ...opts });
  const closeDialog = () => setDialog(EMPTY_DIALOG);

  const handleDelete = () => confirm({
    title: 'Delete branch',
    message: `Are you sure you want to delete branch "${branch.version}"? This cannot be undone.`,
    danger: true,
    confirmLabel: 'Delete',
    onConfirm: () => {
      closeDialog();
      apiDeleteBranch(branch.version)
        .then(() => { setAlert('Delete branch', `Branch "${branch.version}" deleted`, 'success'); reloadData?.(); })
        .catch((e) => setAlert('Delete error', e, 'danger'));
    },
  });

  const handleCreateBranch = () => confirm({
    title: 'Create branches',
    message: `Trigger branch creation workflow for "${branch.version}" from main?`,
    confirmLabel: 'Create',
    onConfirm: () => {
      closeDialog();
      apiTriggerCreateBranch(branch.version, { base_branch: 'main' })
        .then(() => { setAlert('Create branch', `Branch creation triggered for "${branch.version}"`, 'success'); reloadData?.(); })
        .catch((e) => setAlert('Create branch error', e, 'danger'));
    },
  });

  const handleToggleNightly = () => {
    const enabling = !branch.nightly_enabled;
    apiUpdateBranch(branch.version, { nightly_enabled: enabling })
      .then(() => { setAlert('Nightly', `Nightly ${enabling ? 'enabled' : 'disabled'} for "${branch.version}"`, 'success'); reloadData?.(); })
      .catch((e) => setAlert('Nightly error', e, 'danger'));
  };

  const handleSetStatus = (status) => {
    apiUpdateBranch(branch.version, { status })
      .then(() => { setAlert('Status', `Branch "${branch.version}" set to ${status}`, 'success'); reloadData?.(); })
      .catch((e) => setAlert('Status error', e, 'danger'));
  };

  const handleSetToRelease = () => {
    apiUpdateBranch(branch.version, { to_release: true })
      .then(() => { setAlert('To-release target', `Branch "${branch.version}" set as to-release target`, 'success'); reloadData?.(); })
      .catch((e) => setAlert('Set to release error', e, 'danger'));
  };

  if (!branch?.status) return null;

  const hasBranched = branch.branching_ops && Object.keys(branch.branching_ops).length > 0;

  const buttons = {
    active: (
      <>
        {!hasBranched && <Btn variant="primary" size={size} icon={ICO_BRANCH} onClick={handleCreateBranch}>Create branches</Btn>}
        <Btn variant="secondary" size={size} icon={ICO_MOON} onClick={handleToggleNightly}>
          {branch.nightly_enabled ? 'Disable nightly' : 'Enable nightly'}
        </Btn>
        <Btn variant="warn" size={size} onClick={() => handleSetStatus('inactive')}>Deactivate</Btn>
      </>
    ),
    branching: (
      <Btn variant="secondary" size={size} icon={ICO_MOON} onClick={handleToggleNightly}>
        {branch.nightly_enabled ? 'Disable nightly' : 'Enable nightly'}
      </Btn>
    ),
    inactive: (
      <>
        <Btn variant="success" size={size} icon={ICO_CHECK} onClick={() => handleSetStatus('active')}>Activate</Btn>
        <Btn variant="danger" size={size} icon={ICO_TRASH} onClick={handleDelete}>Delete</Btn>
      </>
    ),
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {showToReleaseAction && !branch.to_release && (
          <Btn variant="soft" size={size} icon={ICO_TAG} onClick={handleSetToRelease}>Set as To-Release target</Btn>
        )}
        {buttons[branch.status] ?? null}
      </div>
      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmLabel={dialog.confirmLabel}
        danger={dialog.danger}
        onConfirm={dialog.onConfirm}
        onCancel={closeDialog}
      />
    </>
  );
}
